import { FormGroup } from '@angular/forms';

import { JsonSchema } from '@core/models/transformers.model';

import { LocaleTranslationObject } from '@shared/types/translation.type';

export enum FormBuilderFieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  TIME = 'time',
  DURATION = 'duration',
  ITEMS = 'items',
  AUTO_COMPLETE = 'auto-complete',
  CONST = 'const',
  MULTIPLAY = 'multiplay',
  ENTITY_AUTO_COMPLETE = 'entity-auto-complete',
  CUSTOM_MAP = 'custom-map',
  TAGGED_SECTION = 'tagged-section',
  TAGS_INPUT = 'tags-input',
  LABELS_MAP = 'labels-map',
  TEXT_LIST = 'text-list',
  GEOJSON_POLYGON = 'geojson-polygon',
  POLYLINE = 'polyline',
  COLOR = 'color',
  COMPATIBILITY_CFG = 'compatibility-cfg'
}

export enum CustomValueStrategy {
  APPEND_AS_IS = 'append_as_is',
  WRAP_INDIVIDUAL = 'wrap_individual'
}

export interface CustomValueConfig {
  strategy: CustomValueStrategy;
  wrapperProperty?: string;
}

export interface FormBuilderBase {
  id: string;
  required: boolean;
  isArray: boolean;
  label: LocaleTranslationObject;
  description?: LocaleTranslationObject;
  default?: string | number | boolean;
  fieldOrder: number;
  /** When true, the field is read-only and hidden in edit forms. */
  immutable?: boolean;
  /**
   * A nullable list the user empties is submitted as cleared (`null`) rather than `[]`,
   * because the schema forbids an empty list (`minItems` ≥ 1) or the backend reads `[]`
   * differently from the "no restriction" the form promises.
   */
  emptyArrayIsNull?: boolean;
}

export interface FieldString {
  id?: string;
  type: FormBuilderFieldType.STRING;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
}

export interface FieldNumber {
  id?: string;
  type: FormBuilderFieldType.NUMBER;
  format: string;
  min?: number;
  max?: number;
  step?: number;
}

export interface FieldBoolean {
  id?: string;
  type: FormBuilderFieldType.BOOLEAN;
  indeterminate?: boolean;
}

export interface FieldDate {
  id?: string;
  type: FormBuilderFieldType.DATE;
  minDate?: Date;
  maxDate?: Date;
  needTime?: boolean;
}

export interface FieldAutoComplete {
  id?: string;
  type: FormBuilderFieldType.AUTO_COMPLETE;
  options?: string[];
  allowCustomValues?: boolean;
  customValueConfig?: CustomValueConfig;
}

export interface FieldEntityAutoComplete {
  id?: string;
  type: FormBuilderFieldType.ENTITY_AUTO_COMPLETE;
  entityType: string;
}

export interface FieldTime {
  id?: string;
  type: FormBuilderFieldType.TIME;
  minTime?: string;
  maxTime?: string;
  /** The schema's `partial-time` pattern, enforced on the control. */
  pattern?: string;
}

export interface FieldItems {
  id?: string;
  type: FormBuilderFieldType.ITEMS;
  values: { label: string; value: string | number }[];
}

export interface FieldDuration {
  id?: string;
  type: FormBuilderFieldType.DURATION;
}

export interface ConstField {
  id?: string;
  type: FormBuilderFieldType.CONST;
  const: string | number | boolean | null;
}

export interface FieldCustomMap {
  id?: string;
  type: FormBuilderFieldType.CUSTOM_MAP;
  valueTypes?: ('string' | 'number' | 'boolean')[];
}

export interface FieldTaggedSection {
  id?: string;
  type: FormBuilderFieldType.TAGGED_SECTION;
  discriminatorField: string;
  discriminatorValue: string;
  fields: FormBuilderItem[];
}

export interface FieldTagsInput {
  id?: string;
  type: FormBuilderFieldType.TAGS_INPUT;
}

export interface FieldLabelsMap {
  id?: string;
  type: FormBuilderFieldType.LABELS_MAP;
}

export interface FieldTextList {
  id?: string;
  type: FormBuilderFieldType.TEXT_LIST;
}

export interface FieldGeoJsonPolygon {
  id?: string;
  type: FormBuilderFieldType.GEOJSON_POLYGON;
}

export interface FieldPolyline {
  id?: string;
  type: FormBuilderFieldType.POLYLINE;
}

export interface FieldColor {
  id?: string;
  type: FormBuilderFieldType.COLOR;
}

export interface FieldCompatibilityCfg {
  id?: string;
  type: FormBuilderFieldType.COMPATIBILITY_CFG;
  /** The carrier's root schema: the `x-compatibility-*` extensions the editor reads live there. */
  carrierSchema: JsonSchema;
}

export interface OneOfField {
  type: FormBuilderFieldType.MULTIPLAY;
  oneOf: FieldList[];
  /**
   * The field is a nullable duration, whose control cannot show `null` (it reads
   * 0 seconds), so `oneOf` is `[duration, null const]` and the toggle reads
   * "Value | None" instead of repeating the field's label.
   */
  nullableDuration?: boolean;
}

export type FieldList =
  | FieldString
  | FieldPolyline
  | FieldNumber
  | FieldBoolean
  | FieldDate
  | FieldItems
  | FieldTime
  | FieldAutoComplete
  | FieldEntityAutoComplete
  | FieldDuration
  | OneOfField
  | ConstField
  | FieldCustomMap
  | FieldTaggedSection
  | FieldTagsInput
  | FieldLabelsMap
  | FieldTextList
  | FieldGeoJsonPolygon
  | FieldColor
  | FieldCompatibilityCfg;

export type FormBuilderData = FormBuilderBase & FieldList;

export interface FormBuilderSection extends FormBuilderBase {
  items: FormBuilderItem[];
  /**
   * True when the underlying schema accepts `null` for this object (e.g. an
   * `anyOf: [{...}, {type: 'null'}]`). Used on submission to send an explicit
   * `null` when the section is removed, instead of omitting it.
   */
  nullable?: boolean;
}

export interface FormBuilderSchema {
  items: FormBuilderItem[];
}

export type FormBuilderItem = FormBuilderData | FormBuilderSection;

/** Represents a FormGroup with an isRequired property */
export interface FormGroupOptional extends FormGroup {
  isRequired: boolean;
}
